import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";
import { WorkspaceSelectionDialog } from "@/components/WorkspaceSelectionDialog";
import { useTheme } from "next-themes";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { LayoutDashboard, FileText, BookOpen, MessageSquare, Building2, Loader2 } from "lucide-react";
import queueOSLogoDark from "@/assets/queueos-logo-dark.svg";
import queueOSLogoLight from "@/assets/queueos-logo-light.svg";

export default function Auth() {
  const { resolvedTheme } = useTheme();
  const isMobile = useIsMobile();
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  const { user, isLoading, signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("signin");

  useEffect(() => {
    if (!isLoading && user) {
      const redirectUrl = searchParams.get('redirect');
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl));
      }
    }
  }, [user, isLoading, searchParams, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { error, requiresMFA } = await signIn(email, password);
    
    if (error) {
      toast.error(error.message || "Failed to sign in");
    } else if (requiresMFA) {
      toast.info("MFA verification required");
    } else {
      toast.success("Signed in successfully!");
      navigate("/dashboard");
    }
    
    setIsSubmitting(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { error } = await signUp(email, password, name);
    
    if (error) {
      toast.error(error.message || "Failed to sign up");
    } else {
      toast.success("Account created! Please check your email to verify your account.");
    }
    
    setIsSubmitting(false);
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    const { error } = await signInWithGoogle();
    if (error) {
      toast.error(error.message || "Failed to sign in with Google");
      setIsSubmitting(false);
    }
  };

  // Apps configuration for logged-in users
  const getAvailableApps = () => {
    return [
      {
        id: "getting-started",
        title: "Getting Started",
        description: "Learn how to use QueueOS",
        icon: BookOpen,
        onClick: () => window.open('https://docs.queueos.com/en/articles/12330797-welcome-to-queueos', '_blank'),
        color: "bg-gradient-to-br from-purple-500 to-purple-600"
      },
      {
        id: "feedback",
        title: "Feedback",
        description: "Share your thoughts on QueueOS",
        icon: MessageSquare,
        onClick: () => window.open('https://queueos.com/forms/feedback-on-queueos', '_blank'),
        color: "bg-gradient-to-br from-orange-500 to-orange-600"
      },
      {
        id: "dashboard",
        title: "Dashboard",
        description: "Main dashboard and overview",
        icon: LayoutDashboard,
        href: "/dashboard",
        color: "bg-gradient-to-br from-blue-500 to-blue-600"
      },
      {
        id: "workspaces",
        title: "Workspaces",
        description: "Select and navigate to a workspace",
        icon: Building2,
        onClick: () => setWorkspaceDialogOpen(true),
        color: "bg-gradient-to-br from-cyan-500 to-cyan-600"
      },
      {
        id: "documents",
        title: "Documents",
        description: "Create and edit rich text documents",
        icon: FileText,
        href: "/documents",
        color: "bg-gradient-to-br from-emerald-500 to-emerald-600"
      }
    ];
  };

  const handleAppClick = (app: any) => {
    if (app.href) {
      navigate(app.href);
    } else if (app.onClick) {
      app.onClick();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-purple-400 via-purple-500 to-blue-500">
          <div className="absolute inset-0 bg-gradient-to-tr from-pink-300 via-purple-300 to-indigo-400 opacity-70 animate-pulse"></div>
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite]"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_1s]"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_2s]"></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  // For authenticated users, show app launcher
  if (user) {
    const apps = getAvailableApps();
    
    return (
      <>
        <SEO 
          title="App Launcher | queueOS"
          description="Access your queueOS apps and workspaces."
          url="https://queueos.com/auth"
          noindex={true}
        />
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-purple-400 via-purple-500 to-blue-500">
            <div className="absolute inset-0 bg-gradient-to-tr from-pink-300 via-purple-300 to-indigo-400 opacity-70 animate-pulse"></div>
            <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite]"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_1s]"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_2s]"></div>
          </div>

          <div className="container mx-auto px-6 flex items-center justify-center min-h-screen relative z-10">
            <Card className="w-full max-w-2xl">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-6">
                  <img 
                    src={resolvedTheme === "dark" ? queueOSLogoLight : queueOSLogoDark} 
                    alt="queueOS Logo" 
                    className="h-14 w-auto" 
                  />
                </div>
                <CardTitle className="text-xl">Welcome Back!</CardTitle>
                <CardDescription>Choose where you'd like to go</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                  {apps.map((app) => (
                    <button
                      key={app.id}
                      onClick={() => handleAppClick(app)}
                      className="flex flex-col items-center p-4 rounded-xl border bg-card hover:bg-accent/50 transition-all duration-200 hover:scale-105 hover:shadow-lg group"
                    >
                      <div className={`w-14 h-14 rounded-xl ${app.color} flex items-center justify-center mb-3 shadow-md group-hover:shadow-lg transition-shadow`}>
                        <app.icon className="w-7 h-7 text-white" />
                      </div>
                      <span className="font-medium text-sm text-center">{app.title}</span>
                      <span className="text-xs text-muted-foreground text-center mt-1 line-clamp-2">
                        {app.description}
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <WorkspaceSelectionDialog 
          open={workspaceDialogOpen} 
          onOpenChange={setWorkspaceDialogOpen} 
        />
      </>
    );
  }

  // For non-authenticated users, show sign in/sign up forms
  return (
    <>
      <SEO 
        title="Sign In | queueOS"
        description="Sign in or create an account to access queueOS."
        url="https://queueos.com/auth"
        noindex={true}
      />
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-purple-400 via-purple-500 to-blue-500">
          <div className="absolute inset-0 bg-gradient-to-tr from-pink-300 via-purple-300 to-indigo-400 opacity-70 animate-pulse"></div>
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite]"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_1s]"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-[pulse_4s_ease-in-out_infinite_2s]"></div>
        </div>

        <div className="container mx-auto px-6 flex items-center justify-center min-h-screen relative z-10">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img 
                  src={resolvedTheme === "dark" ? queueOSLogoLight : queueOSLogoDark} 
                  alt="queueOS Logo" 
                  className="h-12 w-auto" 
                />
              </div>
              <CardTitle className="text-2xl">Welcome to queueOS</CardTitle>
              <CardDescription>Sign in to your account or create a new one</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <Input
                        id="signin-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Signing In...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>
                  <div className="mt-4 text-center">
                    <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                      Forgot your password?
                    </Link>
                  </div>
                </TabsContent>
                
                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Name</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
